import { supabaseClient } from '../supabase';
import { logger } from '../logger';
import { sessionTracker } from './sessionTracker';
import { HOTEL_ID } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';

export interface AnalyticsEvent {
  event_type: string;
  event_name: string;
  event_data?: Record<string, any>;
  page_path?: string;
}

type AnalyticsLevel = 'none' | 'essential' | 'performance' | 'all';

class AnalyticsService {
  private static instance: AnalyticsService;
  private enabled: boolean = true;
  private analyticsLevel: AnalyticsLevel = 'essential';
  private isInitialized: boolean = false;
  private eventQueue: AnalyticsEvent[] = [];
  private processingQueue: boolean = false;
  private flushInterval: number | null = null;
  private readonly FLUSH_INTERVAL = 10000; // 10 secondes

  private constructor() {
    this.initialize();
  }

  private initialize(): void {
    // Déterminer si l'analytics est activé par défaut
    this.enabled = import.meta.env.VITE_ENABLE_ANALYTICS !== 'false';
    
    // Déterminer le niveau d'analytics
    this.analyticsLevel = (import.meta.env.VITE_ANALYTICS_LEVEL || 'essential') as AnalyticsLevel;
    
    // Démarrer le traitement de la file d'attente
    this.startQueueProcessing();
    
    this.isInitialized = true;
    
    logger.debug('Service d\'analytics initialisé', {
      category: 'Analytics',
      data: { 
        enabled: this.enabled,
        analyticsLevel: this.analyticsLevel
      }
    });
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public enableAnalytics(): void {
    this.enabled = true;
    logger.debug('Analytics activé', { category: 'Analytics' });
  }

  public disableAnalytics(): void {
    this.enabled = false;
    logger.debug('Analytics désactivé', { category: 'Analytics' });
  }

  public isAnalyticsEnabled(): boolean {
    return this.enabled;
  }

  public setAnalyticsLevel(level: string): void {
    if (['none', 'essential', 'performance', 'all'].includes(level)) {
      this.analyticsLevel = level as AnalyticsLevel;
      logger.debug('Niveau d\'analytics défini', { 
        category: 'Analytics',
        data: { level }
      });
    } else {
      logger.warn('Niveau d\'analytics invalide, utilisation du niveau par défaut', { 
        category: 'Analytics',
        data: { 
          providedLevel: level,
          defaultLevel: this.analyticsLevel
        }
      });
    }
  }

  public getAnalyticsLevel(): AnalyticsLevel {
    return this.analyticsLevel;
  }

  trackSectionView(sectionId: string, sectionType: string, viewType: string, data?: Record<string, any>) {
    this.trackEvent({
      event_type: 'page_view',
      event_name: 'section_view',
      event_data: {
        section_id: sectionId,
        section_type: sectionType,
        view_type: viewType,
        ...data
      }
    });
  }

  trackItemAction(sectionId: string, itemId: string, action: string, itemType: string, data?: Record<string, any>) {
    this.trackEvent({
      event_type: 'interaction',
      event_name: 'item_action',
      event_data: {
        section_id: sectionId,
        item_id: itemId,
        action,
        item_type: itemType,
        ...data
      }
    });
  }

  private shouldTrackEvent(eventType: string): boolean {
    if (!this.enabled) return false;

    switch (this.analyticsLevel) {
      case 'none':
        return false;
      case 'essential':
        return ['error', 'navigation', 'user'].includes(eventType);
      case 'performance':
        return ['error', 'navigation', 'user', 'performance'].includes(eventType);
      case 'all':
        return true;
      default:
        return false;
    }
  }

  private startQueueProcessing(): void {
    if (this.flushInterval !== null) {
      clearInterval(this.flushInterval);
    }

    this.flushInterval = window.setInterval(() => {
      this.processQueue();
    }, this.FLUSH_INTERVAL);

    // Traiter la file d'attente avant de quitter la page
    window.addEventListener('beforeunload', () => {
      this.processQueue(true);
    });
  }

  private async processQueue(isFinal: boolean = false): Promise<void> {
    if (this.processingQueue || this.eventQueue.length === 0) return;

    try {
      this.processingQueue = true;

      // Prendre les événements de la file d'attente
      const events = [...this.eventQueue];
      this.eventQueue = [];

      // Traiter les événements par lots de 10
      const batchSize = 10;
      for (let i = 0; i < events.length; i += batchSize) {
        const batch = events.slice(i, i + batchSize);
        await this.sendEvents(batch);
      }
    } catch (error) {
      logger.error('Erreur lors du traitement de la file d\'attente d\'événements', {
        category: 'Analytics',
        data: { error }
      });

      // En cas d'erreur, remettre les événements dans la file d'attente
      // sauf si c'est le traitement final (beforeunload)
      if (!isFinal) {
        this.eventQueue = [...this.eventQueue, ...this.eventQueue];
      }
    } finally {
      this.processingQueue = false;
    }
  }

  private async sendEvents(events: AnalyticsEvent[]): Promise<void> {
    if (events.length === 0) return;

    try {
      // Récupérer l'ID de session actuel
      const sessionId = sessionTracker.getSessionId();
      if (!sessionId) {
        throw new Error('Session ID not available');
      }

      // Préparer les événements pour l'insertion
      const eventsToInsert = events.map(event => ({
        id: uuidv4(),
        session_id: sessionId,
        hotel_id: HOTEL_ID,
        anonymous_id: sessionTracker.getAnonymousId(),
        event_type: event.event_type,
        event_name: event.event_name,
        event_data: event.event_data || {},
        page_path: event.page_path || window.location.pathname,
        created_at: new Date().toISOString()
      }));

      // Insérer les événements dans la base de données
      const { error } = await supabaseClient
        .from('analytics_events')
        .insert(eventsToInsert);

      if (error) {
        throw error;
      }

      logger.debug('Événements analytics envoyés avec succès', {
        category: 'Analytics',
        data: { count: events.length }
      });
    } catch (error) {
      logger.error('Erreur lors de l\'envoi des événements analytics', {
        category: 'Analytics',
        data: { 
          error,
          events: events.map(e => ({ type: e.event_type, name: e.event_name }))
        }
      });
      throw error;
    }
  }

  public trackEvent(event: AnalyticsEvent): void {
    if (!this.shouldTrackEvent(event.event_type)) {
      return;
    }

    // Ajouter l'événement à la file d'attente
    this.eventQueue.push(event);

    // Si la file d'attente devient trop grande, la traiter immédiatement
    if (this.eventQueue.length >= 20) {
      this.processQueue();
    }

    logger.debug('Événement ajouté à la file d\'attente', {
      category: 'Analytics',
      data: { 
        eventType: event.event_type,
        eventName: event.event_name,
        queueSize: this.eventQueue.length
      }
    });
  }

  public trackPageView(path: string = window.location.pathname): void {
    this.trackEvent({
      event_type: 'navigation',
      event_name: 'page_view',
      event_data: {
        path,
        title: document.title,
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      },
      page_path: path
    });
  }

  public trackError(error: Error, context?: Record<string, any>): void {
    this.trackEvent({
      event_type: 'error',
      event_name: error.name,
      event_data: {
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString()
      }
    });
  }
}

export const analyticsService = AnalyticsService.getInstance();